import { Show, createEffect, createSignal, onMount, type JSX } from "solid-js";
import type { AdminApi } from "./api/client";
import { createNoticeCenter } from "./components/NoticeCenter";
import { AuditModal } from "./features/audit/AuditModal";
import { ActivityPanel, ActivityRecordsModal } from "./features/activity/ActivityPanel";
import { HealthDetailsModal, HealthPanel } from "./features/health/HealthPanel";
import { KeyPanel } from "./features/keys/KeyPanel";
import { QuotaDetailsModal, QuotaPanel } from "./features/quota/QuotaPanel";
import { RelayPanel } from "./features/relays/RelayPanel";
import { ObservedUsagePanel } from "./features/usage/ObservedUsagePanel";
import type { AuditEvent, GatewayKey, GatewayStatus, MeterRecord, ObservedUsageGroup, ObservedUsageWindow, Relay } from "./model";
import { createAsyncPanel } from "./state/async";
import { createObservedUsageController } from "./state/observed-usage";
import { createQuotaBoard } from "./state/quota";
import { formatNumber } from "./utils/format";

interface DashboardProps {
  api: AdminApi;
  initialStatus: GatewayStatus;
  onLogout: () => Promise<void>;
}

export function Dashboard(props: DashboardProps): JSX.Element {
  const notices = createNoticeCenter();
  const health = createAsyncPanel<GatewayStatus>(() => props.api.gateway.status(), props.initialStatus);
  const activity = createAsyncPanel<MeterRecord[]>(() => props.api.gateway.activity(100));
  const observedUsage = createObservedUsageController({ load: props.api.gateway.observedUsage });
  const relays = createAsyncPanel<Relay[]>(() => props.api.relays.list());
  const keys = createAsyncPanel<GatewayKey[]>(() => props.api.keys.list());
  const audit = createAsyncPanel<AuditEvent[]>(() => props.api.gateway.audit(100));
  const quotas = createQuotaBoard(props.api);
  const [refreshingAll, setRefreshingAll] = createSignal(false);
  const [activityOpen, setActivityOpen] = createSignal(false);
  const [healthOpen, setHealthOpen] = createSignal(false);
  const [auditOpen, setAuditOpen] = createSignal(false);
  const [quotaRelay, setQuotaRelay] = createSignal<Relay | null>(null);

  async function refreshRelaysAndQuotas(forceQuota: boolean): Promise<void> {
    const items = await relays.refresh();
    if (items && forceQuota) await quotas.loadActive(items, 4);
  }

  async function refreshAll(): Promise<void> {
    if (refreshingAll()) return;
    setRefreshingAll(true);
    await Promise.allSettled([
      health.refresh(),
      activity.refresh(),
      observedUsage.refresh(),
      keys.refresh(),
      refreshRelaysAndQuotas(true),
    ]);
    setRefreshingAll(false);
  }

  createEffect(() => {
    const items = relays.state().data ?? [];
    const pending = items.filter((relay) => relay.state === "active" && quotas.entry(relay.id).status === "idle");
    if (pending.length > 0) void quotas.loadActive(pending, 4);
  });

  onMount(() => void refreshAll());

  const relayCounts = () => {
    const items = relays.state().data ?? [];
    return {
      active: items.filter((relay) => relay.state === "active").length,
      disabled: items.filter((relay) => relay.state === "disabled").length,
      retired: items.filter((relay) => relay.state === "retired").length,
    };
  };
  const keyCounts = () => {
    const items = keys.state().data ?? [];
    return {
      active: items.filter((key) => key.state === "active").length,
      disabled: items.filter((key) => key.state === "disabled").length,
      revoked: items.filter((key) => key.state === "revoked").length,
    };
  };

  function openAudit(): void {
    setAuditOpen(true);
    if (audit.state().status === "idle") void audit.refresh();
  }

  function selectObservedUsageWindow(window: ObservedUsageWindow): void {
    const current = observedUsage.state();
    if (window === current.window) return;
    void observedUsage.select({ window, group: current.group });
  }

  function selectObservedUsageGroup(group: ObservedUsageGroup): void {
    const current = observedUsage.state();
    if (group === current.group) return;
    void observedUsage.select({ window: current.window, group });
  }

  return (
    <div class="app-shell">
      <header class="topbar">
        <a class="brand" href="#top" aria-label="Agent Relay Control 首页">
          <span class="brand__mark">AR</span>
          <span><strong>Agent Relay</strong><small>Control Surface</small></span>
        </a>
        <div class="topbar__controls">
          <div class="session-chip"><span />已登录</div>
          <nav class="topbar__actions" aria-label="页面操作">
            <button type="button" class="button button--ghost" onClick={openAudit}>操作记录</button>
            <button type="button" class="button button--primary" disabled={refreshingAll()} onClick={() => void refreshAll()}>
              {refreshingAll() ? "正在刷新…" : "全部刷新"}
            </button>
            <button type="button" class="button button--ghost" onClick={() => void props.onLogout()}>退出</button>
          </nav>
        </div>
      </header>

      <main id="top" class="dashboard">
        <div class="bento-grid">
          <HealthPanel panel={health} onOpenDetails={() => setHealthOpen(true)} />

          <section class="bento bento--fleet" aria-labelledby="fleet-heading">
            <header class="bento__header bento__header--compact">
              <div><p class="eyebrow">Fleet Snapshot</p><h2 id="fleet-heading">控制面概览</h2></div>
            </header>
            <div class="fleet-groups">
              <div>
                <span>Relays</span>
                <strong>{formatNumber(relayCounts().active + relayCounts().disabled + relayCounts().retired)}</strong>
                <small>{relayCounts().active} active · {relayCounts().disabled} disabled · {relayCounts().retired} retired</small>
              </div>
              <div>
                <span>Gateway Keys</span>
                <strong>{formatNumber(keyCounts().active + keyCounts().disabled + keyCounts().revoked)}</strong>
                <small>{keyCounts().active} active · {keyCounts().disabled} disabled · {keyCounts().revoked} revoked</small>
              </div>
            </div>
          </section>

          <ObservedUsagePanel
            controller={observedUsage}
            onSelectWindow={selectObservedUsageWindow}
            onSelectGroup={selectObservedUsageGroup}
          />
          <ActivityPanel panel={activity} onOpenRecords={() => setActivityOpen(true)} />
          <QuotaPanel relays={relays.state().data ?? []} board={quotas} onOpenDetails={setQuotaRelay} />
          <RelayPanel api={props.api} panel={relays} keys={keys.state().data ?? []} quotas={quotas} notify={notices.notify} />
          <KeyPanel api={props.api} panel={keys} relays={relays.state().data ?? []} notify={notices.notify} />
        </div>
      </main>

      <notices.Region />

      <Show when={activityOpen()}>
        <ActivityRecordsModal records={activity.state().data ?? []} onClose={() => setActivityOpen(false)} />
      </Show>
      <Show when={healthOpen()}>
        <HealthDetailsModal status={health.state().data} onClose={() => setHealthOpen(false)} />
      </Show>
      <Show when={auditOpen()}>
        <AuditModal
          panel={audit}
          keys={keys.state().data ?? []}
          relays={relays.state().data ?? []}
          onClose={() => setAuditOpen(false)}
        />
      </Show>
      <Show when={quotaRelay()}>
        {(relay) => (
          <QuotaDetailsModal
            relay={relay()}
            entry={quotas.entry(relay().id)}
            onRefresh={() => void quotas.load(relay().id)}
            onClose={() => setQuotaRelay(null)}
          />
        )}
      </Show>
    </div>
  );
}
